import React, { useState, useEffect } from 'react';
import '../styles/Troubleshooter.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Troubleshooter = ({ isOpen, onClose, isFullPage }) => {
  const [diagnostics, setDiagnostics] = useState({
    backend: { status: 'checking', message: '', latency: null },
    websocket: { status: 'checking', message: '' },
    localStorage: { status: 'checking', message: '' },
    browser: { status: 'checking', message: '', info: {} }
  });
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      runDiagnostics();
    }
  }, [isOpen]);

  const runDiagnostics = async () => {
    setIsRunning(true);
    
    // Test Backend Connection
    await testBackend();
    
    // Test WebSocket
    await testWebSocket();
    
    // Test LocalStorage
    testLocalStorage();
    
    // Get Browser Info
    getBrowserInfo();
    
    setIsRunning(false);
  };

  const testBackend = async () => {
    const startTime = Date.now();
    try {
      const response = await fetch(`${BACKEND_URL}/api/chess/new-game`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variant: 'standard',
          white_embodiment: 'instant_king',
          black_embodiment: 'instant_king',
          vs_ai: false
        })
      });
      
      const latency = Date.now() - startTime;
      
      if (response.ok) {
        setDiagnostics(prev => ({
          ...prev,
          backend: {
            status: 'success',
            message: `✅ Backend connected (${latency}ms)`,
            latency
          }
        }));
      } else {
        setDiagnostics(prev => ({
          ...prev,
          backend: {
            status: 'error',
            message: `❌ Backend error: ${response.status}`,
            latency
          }
        }));
      }
    } catch (error) {
      setDiagnostics(prev => ({
        ...prev,
        backend: {
          status: 'error',
          message: `❌ Cannot reach backend: ${error.message}`,
          latency: null
        }
      }));
    }
  };

  const testWebSocket = async () => {
    try {
      const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/ws/test-connection`);
      
      const timeout = setTimeout(() => {
        ws.close();
        setDiagnostics(prev => ({
          ...prev,
          websocket: {
            status: 'warning',
            message: '⚠️ WebSocket connection timeout'
          }
        }));
      }, 5000);
      
      ws.onopen = () => {
        clearTimeout(timeout);
        setDiagnostics(prev => ({
          ...prev,
          websocket: {
            status: 'success',
            message: '✅ WebSocket connection working'
          }
        }));
        ws.close();
      };
      
      ws.onerror = () => {
        clearTimeout(timeout);
        setDiagnostics(prev => ({
          ...prev,
          websocket: {
            status: 'error',
            message: '❌ WebSocket connection failed'
          }
        }));
      };
    } catch (error) {
      setDiagnostics(prev => ({
        ...prev,
        websocket: {
          status: 'error',
          message: `❌ WebSocket error: ${error.message}`
        }
      }));
    }
  };

  const testLocalStorage = () => {
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      
      const settings = localStorage.getItem('chessKingdomSettings');
      setDiagnostics(prev => ({
        ...prev,
        localStorage: {
          status: 'success',
          message: settings 
            ? '✅ LocalStorage working (settings found)'
            : '✅ LocalStorage working (no settings saved)'
        }
      }));
    } catch (error) {
      setDiagnostics(prev => ({
        ...prev,
        localStorage: {
          status: 'error',
          message: `❌ LocalStorage blocked: ${error.message}`
        }
      }));
    }
  };

  const getBrowserInfo = () => {
    const info = {
      browser: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      online: navigator.onLine,
      cookiesEnabled: navigator.cookieEnabled,
      screen: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`
    };
    
    setDiagnostics(prev => ({
      ...prev,
      browser: {
        status: 'success',
        message: '✅ Browser info collected',
        info
      }
    }));
  };

  const resetSettings = () => {
    if (window.confirm('Reset all settings to default? This will clear your customizations.')) {
      localStorage.removeItem('chessKingdomSettings');
      window.dispatchEvent(new Event('settingsChanged'));
      alert('✅ Settings reset! Refresh the page to see default settings.');
    }
  };

  const clearCache = () => {
    if (window.confirm('Clear browser cache? This may help with loading issues.')) {
      localStorage.clear();
      sessionStorage.clear();
      alert('✅ Cache cleared! Please refresh the page (Ctrl+F5 or Cmd+Shift+R).');
    }
  };

  const copyDiagnostics = () => {
    const report = `
Chess Kingdom Diagnostics Report
================================

Backend: ${diagnostics.backend.message}
WebSocket: ${diagnostics.websocket.message}
LocalStorage: ${diagnostics.localStorage.message}

Browser Info:
- User Agent: ${diagnostics.browser.info.browser}
- Platform: ${diagnostics.browser.info.platform}
- Language: ${diagnostics.browser.info.language}
- Online: ${diagnostics.browser.info.online}
- Cookies: ${diagnostics.browser.info.cookiesEnabled}
- Screen: ${diagnostics.browser.info.screen}
- Viewport: ${diagnostics.browser.info.viewport}

Generated: ${new Date().toLocaleString()}
    `.trim();
    
    navigator.clipboard.writeText(report);
    alert('✅ Diagnostics copied to clipboard!');
  };

  if (!isOpen) return null;

  // If full page, just render content without modal wrapper and header
  if (isFullPage) {
    return (
      <div className="troubleshooter-content" style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
        <div className="diagnostics-section">
          <h3>🔍 Connection Diagnostics</h3>
          
          <div className={`diagnostic-item ${diagnostics.backend.status}`}>
            <div className="diagnostic-label">Backend API</div>
            <div className="diagnostic-result">{diagnostics.backend.message}</div>
            {diagnostics.backend.latency && (
              <div className="diagnostic-latency">Latency: {diagnostics.backend.latency}ms</div>
            )}
          </div>

          <div className={`diagnostic-item ${diagnostics.websocket.status}`}>
            <div className="diagnostic-label">WebSocket (Multiplayer)</div>
            <div className="diagnostic-result">{diagnostics.websocket.message}</div>
          </div>

          <div className={`diagnostic-item ${diagnostics.localStorage.status}`}>
            <div className="diagnostic-label">LocalStorage (Settings)</div>
            <div className="diagnostic-result">{diagnostics.localStorage.message}</div>
          </div>

          <div className={`diagnostic-item ${diagnostics.browser.status}`}>
            <div className="diagnostic-label">Browser Compatibility</div>
            <div className="diagnostic-result">{diagnostics.browser.message}</div>
          </div>

          <button 
            className="action-button primary"
            onClick={runDiagnostics}
            disabled={isRunning}
          >
            {isRunning ? '🔄 Running...' : '🔄 Re-run Diagnostics'}
          </button>
        </div>

        <div className="browser-info-section">
          <h3>💻 Browser Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <strong>Platform:</strong> {diagnostics.browser.info.platform}
            </div>
            <div className="info-item">
              <strong>Language:</strong> {diagnostics.browser.info.language}
            </div>
            <div className="info-item">
              <strong>Online:</strong> {diagnostics.browser.info.online ? '✅ Yes' : '❌ No'}
            </div>
            <div className="info-item">
              <strong>Cookies:</strong> {diagnostics.browser.info.cookiesEnabled ? '✅ Enabled' : '❌ Disabled'}
            </div>
            <div className="info-item">
              <strong>Screen:</strong> {diagnostics.browser.info.screen}
            </div>
            <div className="info-item">
              <strong>Viewport:</strong> {diagnostics.browser.info.viewport}
            </div>
          </div>
        </div>

        <div className="actions-section">
          <h3>🛠️ Quick Fixes</h3>
          
          <button className="action-button warning" onClick={resetSettings}>
            🔄 Reset Settings to Default
          </button>
          
          <button className="action-button warning" onClick={clearCache}>
            🗑️ Clear Cache & Storage
          </button>
          
          <button className="action-button secondary" onClick={copyDiagnostics}>
            📋 Copy Diagnostics Report
          </button>
        </div>

        <div className="tips-section">
          <h3>💡 Common Issues & Solutions</h3>
          <ul>
            <li><strong>Game loading slowly?</strong> Check your internet connection speed</li>
            <li><strong>Multiplayer not working?</strong> Verify WebSocket status above</li>
            <li><strong>Settings not saving?</strong> Enable cookies and LocalStorage</li>
            <li><strong>Screen too small?</strong> Move history requires &gt; 768px width</li>
            <li><strong>Still having issues?</strong> Try refreshing with Ctrl+F5 (PC) or Cmd+Shift+R (Mac)</li>
          </ul>
        </div>
      </div>
    );
  }

  // Render as modal
  const modalClass = 'troubleshooter-modal';
  const overlayClass = 'troubleshooter-overlay';

  return (
    <div className={overlayClass} onClick={isFullPage ? null : onClose}>
      <div className={modalClass} onClick={(e) => e.stopPropagation()}>
        <div className="troubleshooter-header">
          <h2>🔧 Troubleshooter</h2>
          {!isFullPage && <button className="close-button" onClick={onClose}>×</button>}
        </div>

        <div className="troubleshooter-content">
          <div className="diagnostics-section">
            <h3>🔍 Connection Diagnostics</h3>
            
            <div className={`diagnostic-item ${diagnostics.backend.status}`}>
              <div className="diagnostic-label">Backend API</div>
              <div className="diagnostic-result">{diagnostics.backend.message}</div>
              {diagnostics.backend.latency && (
                <div className="diagnostic-latency">Latency: {diagnostics.backend.latency}ms</div>
              )}
            </div>

            <div className={`diagnostic-item ${diagnostics.websocket.status}`}>
              <div className="diagnostic-label">WebSocket (Multiplayer)</div>
              <div className="diagnostic-result">{diagnostics.websocket.message}</div>
            </div>

            <div className={`diagnostic-item ${diagnostics.localStorage.status}`}>
              <div className="diagnostic-label">LocalStorage (Settings)</div>
              <div className="diagnostic-result">{diagnostics.localStorage.message}</div>
            </div>

            <div className={`diagnostic-item ${diagnostics.browser.status}`}>
              <div className="diagnostic-label">Browser Compatibility</div>
              <div className="diagnostic-result">{diagnostics.browser.message}</div>
            </div>

            <button 
              className="action-button primary"
              onClick={runDiagnostics}
              disabled={isRunning}
            >
              {isRunning ? '🔄 Running...' : '🔄 Re-run Diagnostics'}
            </button>
          </div>

          <div className="browser-info-section">
            <h3>💻 Browser Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <strong>Platform:</strong> {diagnostics.browser.info.platform}
              </div>
              <div className="info-item">
                <strong>Language:</strong> {diagnostics.browser.info.language}
              </div>
              <div className="info-item">
                <strong>Online:</strong> {diagnostics.browser.info.online ? '✅ Yes' : '❌ No'}
              </div>
              <div className="info-item">
                <strong>Cookies:</strong> {diagnostics.browser.info.cookiesEnabled ? '✅ Enabled' : '❌ Disabled'}
              </div>
              <div className="info-item">
                <strong>Screen:</strong> {diagnostics.browser.info.screen}
              </div>
              <div className="info-item">
                <strong>Viewport:</strong> {diagnostics.browser.info.viewport}
              </div>
            </div>
          </div>

          <div className="actions-section">
            <h3>🛠️ Quick Fixes</h3>
            
            <button className="action-button warning" onClick={resetSettings}>
              🔄 Reset Settings to Default
            </button>
            
            <button className="action-button warning" onClick={clearCache}>
              🗑️ Clear Cache & Storage
            </button>
            
            <button className="action-button secondary" onClick={copyDiagnostics}>
              📋 Copy Diagnostics Report
            </button>
          </div>

          <div className="tips-section">
            <h3>💡 Common Issues & Solutions</h3>
            <ul>
              <li><strong>Game loading slowly?</strong> Check your internet connection speed</li>
              <li><strong>Multiplayer not working?</strong> Verify WebSocket status above</li>
              <li><strong>Settings not saving?</strong> Enable cookies and LocalStorage</li>
              <li><strong>Screen too small?</strong> Move history requires > 768px width</li>
              <li><strong>Still having issues?</strong> Try refreshing with Ctrl+F5 (PC) or Cmd+Shift+R (Mac)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Troubleshooter;
